import type { FC } from "react";
import { PHASE1_APP_KINDS, type AppKind } from "common";

export interface AppResources {
  appCpus?: number | null;
  appMemoryMb?: number | null;
  appWallMinutes?: number | null;
}

interface AppResourcesFieldsProps {
  kind: AppKind | null | undefined;
  values: AppResources;
  onChange: (field: keyof AppResources, value: number | null) => void;
}

const FIELDS: {
  field: keyof AppResources;
  id: string;
  label: string;
  min: number;
  max: number;
}[] = [
  { field: "appCpus", id: "app-cpus", label: "CPUs", min: 1, max: 128 },
  {
    field: "appMemoryMb",
    id: "app-memory-mb",
    label: "Memory (MB)",
    min: 256,
    max: 1048576,
  },
  {
    field: "appWallMinutes",
    id: "app-wall-minutes",
    label: "Default walltime (minutes)",
    min: 1,
    max: 10080,
  },
];

/** Empty is null, which clears the value; the input speaks "" for it. */
const AppResourcesFields: FC<AppResourcesFieldsProps> = ({
  kind,
  values,
  onChange,
}) => {
  if (!kind || !PHASE1_APP_KINDS.includes(kind)) return null;
  return (
    <div>
      <p className="block text-sm font-medium leading-6 text-gray-900">
        Cluster resources
      </p>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {FIELDS.map(({ field, id, label, min, max }) => (
          <div key={field}>
            <label
              htmlFor={id}
              className="block text-sm leading-6 text-gray-900">
              {label}
            </label>
            <input
              id={id}
              type="number"
              inputMode="numeric"
              step={1}
              min={min}
              max={max}
              value={values[field] ?? ""}
              onChange={e =>
                onChange(
                  field,
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        ))}
      </div>
      <p className="mt-1 text-sm text-gray-500">
        CPUs and memory are the least a cluster launch gets. Walltime is the
        starting value a user may change.
      </p>
    </div>
  );
};

export default AppResourcesFields;
